import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Anchor, Button, Center, Group, Image, Loader, PasswordInput, Stack, Text } from "@mantine/core"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useCallback, useEffect, useState } from "react"
import { Logo } from "../components/Logo"
import { SharedDocumentFrame } from "../components/SharedDocumentFrame"
import { fetchShareHtml, fetchShareMeta, type IShareBrand } from "../lib/api/shares"
import { AuthShell } from "./-auth-shared/AuthShell"

import classes from "./share.$token.module.css"

/** Public product homepage for the brand footer (the distribution loop). */
function productHref(product: string | undefined): string {
  return product === "voxcanon" ? "https://voxcanon.com" : "https://lexicanon.com"
}

function ShareGate({
  brand,
  title,
  children,
}: {
  brand?: IShareBrand
  title: React.ReactNode
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <AuthShell
      logo={brand?.logoDataUri}
      appName={brand?.name}
      product={brand?.product === "voxcanon" ? "voxcanon" : "lexicanon"}
      title={title}
    >
      {children}
    </AuthShell>
  )
}

function SharePage(): React.JSX.Element {
  const { token } = Route.useParams()
  const { i18n } = useLingui()

  const [html, setHtml] = useState<string | null>(null)
  const [password, setPassword] = useState("")
  const [gateError, setGateError] = useState<string | null>(null)
  const [loadingDoc, setLoadingDoc] = useState(false)

  const metaQuery = useQuery({
    queryKey: ["share-meta", token],
    queryFn: () => fetchShareMeta(token),
    retry: false,
  })
  const meta = metaQuery.data
  const brand = meta?.brand

  const loadDocument = useCallback(
    async (pw?: string): Promise<void> => {
      setLoadingDoc(true)
      setGateError(null)
      const result = await fetchShareHtml(token, pw)
      setLoadingDoc(false)
      if (result.ok && result.html) {
        setHtml(result.html)
        return
      }
      // 401 → password required/wrong; anything else → a generic load failure.
      setGateError(
        result.status === 401
          ? i18n._(msg`That password is incorrect.`)
          : (result.message ?? i18n._(msg`This shared content is no longer available.`)),
      )
    },
    [token, i18n],
  )

  // Open (no-password) shares load their document as soon as the metadata says ok.
  useEffect(() => {
    if (meta?.state === "ok" && html === null && !loadingDoc) {
      void loadDocument()
    }
  }, [meta?.state, html, loadingDoc, loadDocument])

  const handleUnlock = useCallback(
    (e: React.FormEvent): void => {
      e.preventDefault()
      if (password.length === 0) return
      void loadDocument(password)
    },
    [password, loadDocument],
  )

  // --- Rendered document (open or unlocked) -------------------------------
  if (html !== null) {
    return (
      <div className={classes.shell}>
        <div className={classes.bar}>
          {brand?.logoDataUri ? (
            <Image src={brand.logoDataUri} alt={brand.name} className={classes.barLogo} fit="contain" />
          ) : (
            <Logo size={26} />
          )}
          <Text fw={600}>{brand?.name}</Text>
        </div>
        <div className={classes.frameWrap}>
          <SharedDocumentFrame html={html} title={i18n._(msg`Shared document`)} />
        </div>
        <div className={classes.footer}>
          <Text size="xs" c="dimmed">
            <Trans>
              Generated with{" "}
              <Anchor href={productHref(brand?.product)} target="_blank" rel="noopener noreferrer" size="xs">
                {brand?.name ?? "Lexicanon"}
              </Anchor>
            </Trans>
          </Text>
        </div>
      </div>
    )
  }

  // --- Loading metadata ---------------------------------------------------
  if (metaQuery.isLoading) {
    return (
      <ShareGate title={<Trans>Opening shared link…</Trans>}>
        <Center py="md">
          <Loader />
        </Center>
      </ShareGate>
    )
  }

  // --- Sign-in gate (members-only links) ----------------------------------
  if (meta?.state === "login-required") {
    const loginHref = `/login?redirect=${encodeURIComponent(`/share/${token}`)}`
    return (
      <ShareGate brand={brand} title={<Trans>Sign in to view</Trans>}>
        <Stack>
          <Text size="sm" c="dimmed">
            <Trans>
              Whoever shared this limited it to signed-in members of their workspace. Sign in with your account to view
              it. If you're already signed in, your account isn't a member of that workspace.
            </Trans>
          </Text>
          <Button component="a" href={loginHref} fullWidth>
            <Trans>Sign in</Trans>
          </Button>
        </Stack>
      </ShareGate>
    )
  }

  // --- Password gate ------------------------------------------------------
  if (meta?.state === "password") {
    return (
      <ShareGate brand={brand} title={<Trans>Password required</Trans>}>
        <form onSubmit={handleUnlock}>
          <Stack>
            <Text size="sm" c="dimmed">
              <Trans>This link is protected. Enter the password you were given to view it.</Trans>
            </Text>
            {gateError ? (
              <Alert color="danger" variant="light">
                {gateError}
              </Alert>
            ) : null}
            <PasswordInput
              label={i18n._(msg`Password`)}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              autoFocus
              required
            />
            <Button type="submit" fullWidth loading={loadingDoc} disabled={password.length === 0}>
              <Trans>View</Trans>
            </Button>
          </Stack>
        </form>
      </ShareGate>
    )
  }

  // --- Terminal error states ---------------------------------------------
  const errorMessage = ((): React.ReactNode => {
    switch (meta?.state) {
      case "expired":
        return <Trans>This link has expired.</Trans>
      case "revoked":
        return <Trans>This link has been turned off by its owner.</Trans>
      case "unshareable":
        return <Trans>This meeting can't be shared publicly.</Trans>
      case "not-found":
        return <Trans>This link is not valid.</Trans>
      default:
        return <Trans>We couldn't open this link. Please try again later.</Trans>
    }
  })()

  return (
    <ShareGate brand={brand} title={<Trans>Link unavailable</Trans>}>
      <Stack>
        <Alert color="danger" variant="light">
          {errorMessage}
        </Alert>
        {gateError ? (
          <Alert color="danger" variant="light">
            {gateError}
          </Alert>
        ) : null}
        <Group justify="center">
          <Anchor href={productHref(brand?.product)} target="_blank" rel="noopener noreferrer" size="sm">
            <Trans>Learn more</Trans>
          </Anchor>
        </Group>
      </Stack>
    </ShareGate>
  )
}

export const Route = createFileRoute("/share/$token")({
  component: SharePage,
})
